/**
 * Shared hostname normalization for white-label products (Studio, AmeriCon).
 */
const LOCALHOST_HOSTNAMES = new Set(['localhost', '127.0.0.1', '::1']);

export function normalizeTenantHostname(input: string): string {
  const trimmed = input.trim().toLowerCase();
  if (!trimmed) return '';
  const withoutScheme = trimmed.replace(/^[a-z]+:\/\//, '');
  const hostnameWithPath = withoutScheme.split('/')[0] || '';
  const hostname = hostnameWithPath.split('@').pop() || '';
  return hostname.replace(/:\d+$/, '').replace(/\.+$/, '');
}

export function isLocalDevHostname(input: string): boolean {
  const hostname = normalizeTenantHostname(input);
  return !hostname || LOCALHOST_HOSTNAMES.has(hostname);
}

/**
 * Bare IPv4/IPv6 literal, e.g. the dev VM reached by address instead of hostname.
 * Tenant hosts are always names, so an IP must never be read as a white-label host.
 */
export function isIpLiteralTenantHostname(input: string): boolean {
  const hostname = normalizeTenantHostname(input);
  if (!hostname) return false;
  if (/^\d{1,3}(\.\d{1,3}){3}$/.test(hostname)) return true;
  // Port is already stripped, so a remaining colon means IPv6 (bracketed or bare).
  return hostname.includes(':');
}

/**
 * Labels under `americanpolicetraining.com` that must never resolve to a tenant
 * site. Covers APT product surfaces plus infrastructure hosts (`cdn`, `mail`, …)
 * so a white-label router can't hijack them. Shared by Studio and AmeriCon —
 * keep one list, not per-product copies that drift.
 *
 * Product zone roots (`sites.`, `conferences.`) are matched before this check,
 * so reserving them here does not affect real tenant hostnames.
 */
const RESERVED_APT_TENANT_SUBDOMAINS = new Set([
  // APT product surfaces
  'admin', 'agency', 'americon', 'api', 'app', 'author', 'backoffice', 'blog',
  'certification', 'chat', 'conferences', 'cwv', 'dash', 'dev', 'docs', 'eat',
  'event', 'events', 'faq', 'gallery', 'instructor', 'k9', 'k9-ops', 'me',
  'seo', 'shop', 'sites', 'support', 'tacticore', 'teach', 'tics', 'training',
  'travel', 'vendor', 'venue', 'www',
  // Infrastructure / operational hosts
  'assets', 'autodiscover', 'cdn', 'ci', 'download', 'downloads', 'edge', 'files',
  'ftp', 'git', 'grafana', 'images', 'img', 'imap', 'mail', 'media', 'metrics',
  'monitor', 'mx', 'ns1', 'ns2', 'origin', 'preview', 'qa', 'smtp', 'stage',
  'staging', 'static', 'status', 'test', 'uploads', 'vpn', 'webmail',
]);

/** True when `label` is an APT product or infrastructure subdomain, never a tenant. */
export function isReservedAptTenantSubdomain(label: string): boolean {
  return RESERVED_APT_TENANT_SUBDOMAINS.has(label.trim().toLowerCase());
}

export { LOCALHOST_HOSTNAMES, RESERVED_APT_TENANT_SUBDOMAINS };
